import {
  Button, Card, DataTable, EmptyState, ErrorState, Field, LARGEST_PAGE, Pager, Permissions, Row, Select, Stack,
  useApi, useClampedPage, useI18n, usePermission, usePrintedFile,
  type CareEntryKind, type ReportMonthQuery,
} from '@daghfal/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

/** Roster rows to a page; a nursery's register runs to a few hundred children. */
const ROSTER_PAGE_SIZE = 50;

/** The months of a year. */
const MONTHS_IN_YEAR = 12;

/** `YYYY-MM` as its year and month. */
function splitMonth(value: string): [number, number] {
  return value.split('-').map(Number) as [number, number];
}

/** A year and a month as `YYYY-MM`. */
function joinMonth(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** `YYYY-MM` as the API's month query. */
function monthQuery(value: string): ReportMonthQuery {
  const [year, month] = splitMonth(value);
  return { year, month };
}

/** Every whole number from `first` to `last`, in that order, either way round. */
function span(first: number, last: number): number[] {
  const step = first <= last ? 1 : -1;
  return Array.from({ length: Math.abs(last - first) + 1 }, (_, index) => first + index * step);
}

/**
 * The months a report may name, as `YYYY-MM`: from the start of the caller's history to the current month (Q33,
 * Q35). That is the current academic year's first month, or the earliest year's for someone who reads the archive,
 * the same day the API cuts a report at. Until the years are read, the current month alone.
 */
function useMonthBounds(): { first: string; last: string } {
  const api = useApi();
  const { today } = useI18n();
  const readsArchive = usePermission(Permissions.academic.viewArchive);
  const years = useQuery({
    queryKey: ['academic-years', { pageSize: LARGEST_PAGE }],
    queryFn: () => api.academicYears.list({ pageSize: LARGEST_PAGE }),
  });

  const last = today().slice(0, 7);
  const starts = (years.data?.items ?? [])
    .filter((year) => (readsArchive ? year.status !== 'Planned' : year.status === 'Current'))
    .map((year) => year.startDate.slice(0, 7))
    .sort();
  const first = starts[0] ?? last;
  return { first: first < last ? first : last, last };
}

/** Picks a month by its name and its year, within the months the caller's history reaches. */
function MonthField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t, formatMonthName, formatYear } = useI18n();
  const { first, last } = useMonthBounds();
  const [year, month] = splitMonth(value);
  const [firstYear, firstMonth] = splitMonth(first);
  const [lastYear, lastMonth] = splitMonth(last);

  // A year whose months the bounds cut keeps the nearest month they leave.
  const pick = (nextYear: number, nextMonth: number) => {
    const wanted = joinMonth(nextYear, nextMonth);
    onChange(wanted < first ? first : wanted > last ? last : wanted);
  };

  return (
    <>
      <Field label={t('reports.month')}>
        {(props) => (
          <Select {...props} value={month} onChange={(event) => pick(year, Number(event.target.value))}>
            {span(year === firstYear ? firstMonth : 1, year === lastYear ? lastMonth : MONTHS_IN_YEAR).map((option) => (
              <option key={option} value={option}>{formatMonthName(option)}</option>
            ))}
          </Select>
        )}
      </Field>
      <Field label={t('reports.year')}>
        {(props) => (
          <Select {...props} value={year} onChange={(event) => pick(Number(event.target.value), month)}>
            {span(lastYear, firstYear).map((option) => (
              <option key={option} value={option}>{formatYear(option)}</option>
            ))}
          </Select>
        )}
      </Field>
    </>
  );
}

/** The print button every report shares: a staff document, watermarked for whoever prints it (C8). */
function PrintButton({ fileKey, fetch }: { fileKey: string; fetch: () => Promise<Blob> }) {
  const { t } = useI18n();
  const printed = usePrintedFile();
  const busy = printed.busy === fileKey;
  return (
    <Stack>
      {printed.error != null && <ErrorState error={printed.error} />}
      <Row>
        <Button variant="secondary" disabled={busy} onClick={() => void printed.open(fileKey, fetch)}>
          {t(busy ? 'action.printing' : 'action.print')}
        </Button>
      </Row>
    </Stack>
  );
}

/** A share of a whole, as a whole percentage; a dash where there is no whole. */
/** A list of entry kinds with a count each, as one cell. */
function useKindList() {
  const { t, formatNumber } = useI18n();
  return (items: { kind: CareEntryKind; count: number }[]) => items
    .map((item) => t('reports.dailyCare.missingItem', { kind: t(`care.kind.${item.kind}`), count: formatNumber(item.count) }))
    .join(t('reports.listSeparator'));
}

function usePercent() {
  const { t, formatNumber } = useI18n();
  return (part: number, whole: number) =>
    whole === 0 ? '—' : t('reports.percent', { value: formatNumber(Math.round((part * 100) / whole)) });
}

/** A class's attendance over one month, child by child (FR-ADM-20). */
export function AttendanceReport() {
  const api = useApi();
  const { t, today, formatDate, formatNumber } = useI18n();
  const percent = usePercent();
  const [month, setMonth] = useState(() => today().slice(0, 7));
  const [chosenClass, setChosenClass] = useState<number | ''>('');

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active' }],
    queryFn: () => api.classes.list({ status: 'Active' }),
  });
  const classId = chosenClass === '' ? classes.data?.items[0]?.id : chosenClass;

  const report = useQuery({
    queryKey: ['reports', 'attendance', classId, month],
    queryFn: () => api.reports.attendance(classId!, { ...monthQuery(month), pageSize: LARGEST_PAGE }),
    enabled: classId !== undefined,
  });

  if (classes.isError) return <ErrorState error={classes.error} onRetry={() => void classes.refetch()} />;
  if (classes.data && classId === undefined) return <EmptyState title={t('reports.noClasses')} />;

  const data = report.data;

  return (
    <Stack>
      <Row>
        <Field label={t('class.one')}>
          {(props) => (
            <Select {...props} value={classId ?? ''} onChange={(event) => setChosenClass(Number(event.target.value))}>
              {(classes.data?.items ?? []).map((klass) => (
                <option key={klass.id} value={klass.id}>{klass.name}</option>
              ))}
            </Select>
          )}
        </Field>
        <MonthField value={month} onChange={setMonth} />
      </Row>

      {classId !== undefined && (
        <PrintButton
          fileKey={`attendance-${classId}-${month}`}
          fetch={() => api.reports.attendancePdf(classId, monthQuery(month))}
        />
      )}

      {data && (
        <p className="dg-muted">
          {t('reports.attendance.summary', {
            from: formatDate(data.from),
            to: formatDate(data.to),
            operating: formatNumber(data.operatingDays),
            present: formatNumber(data.present),
            absent: formatNumber(data.absent),
            notified: formatNumber(data.notified),
          })}
        </p>
      )}

      <Card flush>
        <DataTable
          caption={t('reports.attendance.title')}
          rows={data?.items}
          rowKey={(row) => row.childId}
          isLoading={report.isPending && classId !== undefined}
          error={report.error}
          onRetry={() => void report.refetch()}
          empty={t('reports.attendance.empty')}
          columns={[
            { key: 'child', header: t('child.one'), render: (r) => r.name },
            { key: 'days', header: t('reports.attendance.days'), render: (r) => formatNumber(r.days) },
            { key: 'present', header: t('reports.attendance.present'), render: (r) => formatNumber(r.present) },
            { key: 'absent', header: t('reports.attendance.absent'), render: (r) => formatNumber(r.absent) },
            { key: 'notified', header: t('reports.attendance.notified'), render: (r) => formatNumber(r.notified) },
            { key: 'rate', header: t('reports.attendance.rate'), render: (r) => percent(r.present, r.days) },
          ]}
        />
      </Card>
    </Stack>
  );
}

/** The two ways the completeness report counts: by who staffs each class, or by who made each entry. */
type DailyCareView = 'staff' | 'recorders';

const DAILY_CARE_VIEWS: readonly DailyCareView[] = ['staff', 'recorders'];

/**
 * How complete the days were over one month (FR-ADM-20), by class staff or by who recorded (decisions 2026-10-28 #8, 2026-10-29 #4):
 * the first credits a class's days to everyone assigned to it, the second each entry to the person who made it.
 */
export function DailyCareReport() {
  const { t, today } = useI18n();
  const [view, setView] = useState<DailyCareView>('staff');
  const [month, setMonth] = useState(() => today().slice(0, 7));

  return (
    <Stack>
      <Row>
        {DAILY_CARE_VIEWS.map((option) => (
          <Button
            key={option}
            type="button"
            variant={option === view ? 'primary' : 'ghost'}
            aria-pressed={option === view}
            onClick={() => setView(option)}
          >
            {t(`reports.dailyCare.view.${option}`)}
          </Button>
        ))}
      </Row>

      <Row>
        <MonthField value={month} onChange={setMonth} />
      </Row>

      {view === 'staff' ? <ByClassStaff month={month} /> : <ByRecorder month={month} />}
    </Stack>
  );
}

/** Each person assigned to a class: the child-days checked in to it while they were, and how complete. */
function ByClassStaff({ month }: { month: string }) {
  const api = useApi();
  const { t, formatDate, formatNumber } = useI18n();
  const percent = usePercent();
  const kindList = useKindList();

  const report = useQuery({
    queryKey: ['reports', 'daily-care', month],
    queryFn: () => api.reports.dailyCare({ ...monthQuery(month), pageSize: LARGEST_PAGE }),
  });

  return (
    <Stack>
      <PrintButton fileKey={`daily-care-${month}`} fetch={() => api.reports.dailyCarePdf(monthQuery(month))} />

      <p className="dg-muted">
        {report.data
          ? t('reports.dailyCare.span', { from: formatDate(report.data.from), to: formatDate(report.data.to) })
          : null}
        {' '}{t('reports.dailyCare.hint')}
      </p>

      <Card flush>
        <DataTable
          caption={t('reports.dailyCare.title')}
          rows={report.data?.items}
          rowKey={(row) => row.membershipId}
          isLoading={report.isPending}
          error={report.error}
          onRetry={() => void report.refetch()}
          empty={t('reports.dailyCare.empty')}
          columns={[
            { key: 'name', header: t('reports.dailyCare.staff'), render: (r) => r.name },
            { key: 'classes', header: t('reports.dailyCare.classes'), render: (r) => r.classes.join(t('reports.listSeparator')) },
            { key: 'childDays', header: t('reports.dailyCare.childDays'), render: (r) => formatNumber(r.childDays) },
            { key: 'complete', header: t('reports.dailyCare.complete'), render: (r) => formatNumber(r.complete) },
            { key: 'share', header: t('reports.dailyCare.share'), render: (r) => percent(r.complete, r.childDays) },
            { key: 'missing', header: t('reports.dailyCare.missing'), render: (r) => kindList(r.missing) },
          ]}
        />
      </Card>
    </Stack>
  );
}

/** Each person who recorded an entry: what they recorded by kind, and the days they recorded on. */
function ByRecorder({ month }: { month: string }) {
  const api = useApi();
  const { t, formatDate, formatNumber } = useI18n();
  const percent = usePercent();
  const kindList = useKindList();

  const report = useQuery({
    queryKey: ['reports', 'daily-care', 'recorders', month],
    queryFn: () => api.reports.dailyCareRecorders({ ...monthQuery(month), pageSize: LARGEST_PAGE }),
  });

  return (
    <Stack>
      <PrintButton
        fileKey={`daily-care-recorders-${month}`}
        fetch={() => api.reports.dailyCareRecordersPdf(monthQuery(month))}
      />

      <p className="dg-muted">
        {report.data
          ? t('reports.dailyCare.span', { from: formatDate(report.data.from), to: formatDate(report.data.to) })
          : null}
        {' '}{t('reports.dailyCare.recorders.hint')}
      </p>

      <Card flush>
        <DataTable
          caption={t('reports.dailyCare.recorders.title')}
          rows={report.data?.items}
          rowKey={(row) => row.membershipId}
          isLoading={report.isPending}
          error={report.error}
          onRetry={() => void report.refetch()}
          empty={t('reports.dailyCare.recorders.empty')}
          columns={[
            { key: 'name', header: t('reports.dailyCare.staff'), render: (r) => r.name },
            { key: 'classes', header: t('reports.dailyCare.classes'), render: (r) => r.classes.join(t('reports.listSeparator')) },
            { key: 'entries', header: t('reports.dailyCare.recorders.entries'), render: (r) => formatNumber(r.entries) },
            { key: 'byKind', header: t('reports.dailyCare.recorders.byKind'), render: (r) => kindList(r.byKind) },
            { key: 'childDays', header: t('reports.dailyCare.childDays'), render: (r) => formatNumber(r.childDays) },
            { key: 'complete', header: t('reports.dailyCare.complete'), render: (r) => formatNumber(r.complete) },
            { key: 'share', header: t('reports.dailyCare.share'), render: (r) => percent(r.complete, r.childDays) },
          ]}
        />
      </Card>
    </Stack>
  );
}

/** The enrolled children with their class and guardians (FR-ADM-20). */
export function RosterReport() {
  const api = useApi();
  const { t, formatDate, formatPhone } = useI18n();
  const [classId, setClassId] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const only = classId === '' ? {} : { classId };

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active' }],
    queryFn: () => api.classes.list({ status: 'Active' }),
  });

  const roster = useQuery({
    queryKey: ['reports', 'roster', classId, page],
    queryFn: () => api.reports.roster({ ...only, page, pageSize: ROSTER_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  useClampedPage(page, setPage, roster.data?.totalPages);

  return (
    <Stack>
      <Row>
        <Field label={t('class.one')}>
          {(props) => (
            <Select {...props}
              value={classId}
              onChange={(event) => { setClassId(event.target.value === '' ? '' : Number(event.target.value)); setPage(1); }}
            >
              <option value="">{t('reports.allClasses')}</option>
              {(classes.data?.items ?? []).map((klass) => (
                <option key={klass.id} value={klass.id}>{klass.name}</option>
              ))}
            </Select>
          )}
        </Field>
      </Row>

      <PrintButton fileKey={`roster-${classId}`} fetch={() => api.reports.rosterPdf(only)} />

      <Card flush>
        <DataTable
          caption={t('reports.roster.title')}
          rows={roster.data?.items}
          rowKey={(row) => row.childId}
          isLoading={roster.isPending}
          error={roster.error}
          onRetry={() => void roster.refetch()}
          empty={t('reports.roster.empty')}
          columns={[
            {
              key: 'child',
              header: t('child.one'),
              render: (r) => r.preferredName
                ? t('reports.roster.preferred', { name: `${r.firstName} ${r.lastName}`, preferred: r.preferredName })
                : `${r.firstName} ${r.lastName}`,
            },
            { key: 'gender', header: t('reports.roster.gender'), render: (r) => t(`gender.${r.gender}`) },
            { key: 'born', header: t('reports.roster.dateOfBirth'), render: (r) => formatDate(r.dateOfBirth) },
            { key: 'class', header: t('class.one'), render: (r) => r.className ?? '—' },
            { key: 'enrolled', header: t('reports.roster.enrolled'), render: (r) => formatDate(r.enrollmentDate) },
            {
              key: 'guardians',
              header: t('reports.roster.guardians'),
              render: (r) => (
                <Stack>
                  {r.guardians.map((g) => (
                    <span key={`${g.name}-${g.phone ?? ''}`}>
                      {t('reports.roster.guardian', {
                        name: g.name,
                        relationship: t(`guardians.relationship.${g.relationship}`),
                      })}
                      {g.phone && <> · <bdi dir="ltr">{formatPhone(g.phone)}</bdi></>}
                    </span>
                  ))}
                </Stack>
              ),
            },
          ]}
        />
      </Card>

      <Pager page={page} totalPages={roster.data?.totalPages} onPage={setPage} />
    </Stack>
  );
}
